import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Umbrella, Maximize2, Loader2, Calendar, Clock } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';

export default function LeaveBalanceWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  const fetchLeaveBalance = async () => {
    try {
      const data = await apiRequest('/leave/balance');
      setBalance(data);
    } catch (error) {
      console.error('Failed to load leave balance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const leaveTypes = [
    { key: 'annual', label: 'Annual Leave', color: 'bg-blue-500' },
    { key: 'sick', label: 'Sick Leave', color: 'bg-amber-500' },
    { key: 'personal', label: 'Personal Leave', color: 'bg-purple-500' },
  ];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="leave-balance-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Umbrella className="h-5 w-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">Leave Balance</h3>
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {balance ? (
        <div className="space-y-3">
          {leaveTypes.map(type => {
            const available = balance[type.key]?.available || 0;
            const used = balance[type.key]?.used || 0;
            const total = available + used;
            const percentage = total > 0 ? (available / total) * 100 : 0;
            
            return (
              <div key={type.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{type.label}</span>
                  <span className="font-medium">{available} days</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${type.color} rounded-full transition-all`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
          
          {balance.pending_requests > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <Clock className="h-4 w-4" />
                <span>{balance.pending_requests} pending request(s)</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-slate-500 text-sm">
          <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No leave balance data</p>
        </div>
      )}
    </div>
  );
}
